import { Icons, Stack, Typography } from '@healthinal/ui';
import { theme } from '../../theme.ts';
import { SubCard } from '../SubCard.tsx';
import { TruncateText } from '../TruncateText.tsx';
import type { HistoryFieldChange } from './historyTypes.ts';

const inlineArrowSx = { fontSize: '1rem', verticalAlign: 'middle' };
const historyValueMaxWidth = '32ch';
const comparisonColumnSx = { minWidth: 0, flex: '1 1 0%' };

const hasValue = (value: string | null | undefined) => value != null && value !== '';

function HistoryValueText({ value, color }: { value: string; color: 'danger' | 'success' }) {
  return (
    <Typography level={'body-sm'} color={color} sx={{ minWidth: 0, flex: '0 1 auto', maxWidth: historyValueMaxWidth }}>
      <TruncateText>{value}</TruncateText>
    </Typography>
  );
}

export function FieldChangeComparison({
  changes,
  fieldLabel = 'Felder',
  oldValueLabel = 'Alter Wert',
  newValueLabel = 'Neuer Wert',
}: {
  changes: HistoryFieldChange[];
  fieldLabel?: string;
  oldValueLabel?: string;
  newValueLabel?: string;
}) {
  return (
    <Stack direction={'row'} gap={1} sx={{ minWidth: 0 }}>
      <Stack sx={comparisonColumnSx}>
        <Typography level={'body-sm'}>{fieldLabel}</Typography>
        <SubCard sx={{ height: '100%', minWidth: 0 }}>
          <Stack>
            {changes.map((change, index) => (
              <Typography key={`${change.fieldLabel}-${index}`} level={'body-sm'}>
                {change.fieldLabel}
              </Typography>
            ))}
          </Stack>
        </SubCard>
      </Stack>
      <Icons.ArrowForward sx={{ alignSelf: 'center', flexShrink: 0, marginTop: theme.spacing(3) }} />
      <Stack sx={comparisonColumnSx}>
        <Stack direction={'row'} alignItems={'center'} gap={0.5}>
          <Typography level={'body-sm'}>{oldValueLabel}</Typography>
          <Icons.ArrowForward sx={inlineArrowSx} />
          <Typography level={'body-sm'}>{newValueLabel}</Typography>
        </Stack>
        <SubCard sx={{ height: '100%', minWidth: 0 }}>
          <Stack>
            {changes.map((change, index) => {
              const hasOldValue = hasValue(change.oldValue);
              const hasNewValue = hasValue(change.newValue);
              const hasOldAndNewValue = hasOldValue && hasNewValue;

              return (
                <Stack
                  key={`${change.fieldLabel}-${index}`}
                  direction={'row'}
                  alignItems={'center'}
                  gap={0.5}
                  sx={{ minWidth: 0 }}>
                  {hasOldValue && <HistoryValueText value={change.oldValue} color={'danger'} />}
                  {hasOldAndNewValue && <Icons.ArrowForward sx={{ ...inlineArrowSx, flexShrink: 0 }} />}
                  {hasNewValue && <HistoryValueText value={change.newValue} color={'success'} />}
                  {!hasOldValue && !hasNewValue && <Typography level={'body-sm'}>&nbsp;</Typography>}
                </Stack>
              );
            })}
          </Stack>
        </SubCard>
      </Stack>
    </Stack>
  );
}
